import arrayIncludes from 'array-includes';
import objectPath from 'object-path';

/**
 * @typedef   {Array|Object}  TestRule
 * @property  {string} compare The type of comparision that should be performed
 * @property  {string} key The path to the target variable
 * @property  {string|Array} value The value to test against the target key's value
 */

export function testRule(rule, values) {
  const { compare = '=', key, test } = rule;
  const value = objectPath.get(values, key);

  switch (compare.toLowerCase()) {
    case '<':
    case 'lessthan': {
      return (typeof test === 'number') && (typeof value === 'number') && (value < test);
    }

    case '>':
    case 'greaterthan': {
      return (typeof test === 'number') && (typeof value === 'number') && (value > test);
    }

    case '!=':
    case 'not': {
      return test !== value;
    }

    case 'in':
    case 'includes': {
      if (Array.isArray(test)) {
        return arrayIncludes(test, value);
      }
      return String(value).indexOf(test) >= 0;
    }

    case 'notin':
    case 'notincludes': {
      if (Array.isArray(test)) {
        return !arrayIncludes(test, value);
      }
      return String(value).indexOf(test) < 0;
    }

    case 'regex': {
      return !!String(value).match(new RegExp(String(test), 'gi'));
    }

    case 'startswith':
    case '^': {
      return String(value).slice(0, String(test).length) === String(test);
    }

    case 'endswith':
    case '$': {
      return String(value).slice(0 - String(test).length) === String(test);
    }

    case 'undefined': {
      return value === undefined;
    }

    case 'notundefined': {
      return value !== undefined;
    }

    case 'and':
    case 'or':
    case 'xor': {
      /* eslint-disable no-use-before-define */
      return testRules(rule.test, values, rule.compare);
      /* eslint-enable no-use-before-define */
    }
    case '=':
    case 'equals':
    default: {
      return test === value;
    }
  }
}

/**
 * Does value comparison between a test value and a target value.
 * @param  {Array<TestRule>}  rules   The string to test against
 * @param  {Object}           value   The target string to test
 * @param  {String}           compare The type of comparison to do
 * @return {boolean}                  Whether the value matches the test
 */
export default function testRules(rules, values, compare = 'and') {
  if (!Array.isArray(rules)) return testRule(rules, values);
  if (rules.length === 0) return true;

  switch (compare) {
    case 'and':
      return rules.every(rule => testRule(rule, values));
    case 'or':
      return rules.some(rule => testRule(rule, values));
    case 'xor': {
      const res = rules.map(rule => testRule(rule, values));
      return (res.length === 2) && arrayIncludes(res, true) && arrayIncludes(res, false);
    }

    default:
      return false;
  }
}
